import Information from './components/Information';
import Password from './components/Password';
import AcquisitionPreferences from './components/AcquisitionPreferences';
import ApiKeyPanel from './components/ApiKeyPanel';
import SellerApiKeyRedirect from './components/SellerApiKeyRedirect';
import { useEffect, useState } from 'react';
import GoBack from 'components/actions/GoBack';
import UpdateSellerInvoiceInformation from './components/UpdateSellerInvoiceInformation';
import UpdateBuyerInvoiceInformation from './components/UpdateBuyerInvoiceInformation';
import { CONFIG } from 'variables/config';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    MdOutlinePerson,
    MdOutlineReceipt,
    MdOutlineShield,
    MdOutlineLink,
    MdOutlineVpnKey,
    MdOutlineWarningAmber,
    MdOutlineSettings,
} from 'react-icons/md';

type TabType = 'account' | 'invoice' | 'security' | 'integration' | 'apiKey' | 'danger';

const ProfileSetting = () => {
    const nav = useNavigate();
    const [searchParams] = useSearchParams();
    const [wantToDelete, setWantToDelete] = useState(false);
    const [user, setUser] = useState(null);
    const [activeTab, setActiveTab] = useState<TabType>(
        (searchParams.get('tab') as TabType) ?? 'account'
    );
    const [linkedAccount] = useState<{ type: string; email: string } | null>(
        () => {
            const type = localStorage.getItem('linked-user-type');
            const email = localStorage.getItem('linked-user-email');
            return type ? { type, email: email || '' } : null;
        }
    );
    const [switchLoading, setSwitchLoading] = useState(false);
    const [switchError, setSwitchError] = useState('');

    const handleLogout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        nav('/auth/sign-in');
    };

    const handleActivateAccountSwitch = async () => {
        setSwitchLoading(true);
        setSwitchError('');
        try {
            const token = localStorage.getItem('token');
            const res = await axios.post(
                `${CONFIG.apiUrl}/account/switch/activate`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            localStorage.setItem('linked-token', res.data.linkedToken);
            localStorage.setItem('linked-user-type', res.data.linkedUserType);
            localStorage.setItem('linked-user-email', res.data.linkedUserEmail);
            // Reload so the navbar (which only reads localStorage once, on mount)
            // picks up the newly-activated bridge and shows the switch button.
            window.location.reload();
        } catch (err: any) {
            setSwitchError(
                err.response?.data?.error || "Impossible d'activer le compte lié."
            );
        } finally {
            setSwitchLoading(false);
        }
    };

    const initUser = () => {
        const token = localStorage.getItem('token');
        const config = {
            headers: { Authorization: `Bearer ${token}` },
        };
        axios
            .get(`${CONFIG.apiUrl}/user/me`, config)
            .then((res) => {
                const user = res.data;
                setUser(user);
                if (user?.email) localStorage.setItem('user-email', user.email);
            })
            .catch((err) => {
                console.log(err);
            });
    };

    useEffect(() => {
        initUser();
    }, []);

    const tabs = [
        {
            id: 'account' as TabType,
            label: 'Compte',
            icon: MdOutlinePerson,
            activeClasses: 'border-leadrs-purple-focus bg-leadrs-purple-soft text-leadrs-brand',
            iconBg: 'bg-leadrs-purple-focus',
        },
        {
            id: 'invoice' as TabType,
            label: 'Facturation',
            icon: MdOutlineReceipt,
            activeClasses: 'border-leadrs-yellow-focus bg-leadrs-yellow-soft text-leadrs-yellow-focus',
            iconBg: 'bg-leadrs-yellow-focus',
        },
        {
            id: 'security' as TabType,
            label: 'Sécurité',
            icon: MdOutlineShield,
            activeClasses: 'border-leadrs-green-focus bg-leadrs-green-soft text-leadrs-green-focus',
            iconBg: 'bg-leadrs-green-focus',
        },
        ...(user?.type === 'seller'
            ? [
                  {
                      id: 'integration' as TabType,
                      label: 'Intégration',
                      icon: MdOutlineLink,
                      activeClasses:
                          'border-leadrs-brown-focus bg-leadrs-brown-soft text-leadrs-brown-focus',
                      iconBg: 'bg-leadrs-brown-focus',
                  },
              ]
            : []),
        ...(user?.type === 'buyer' || user?.type === 'seller'
            ? [
                  {
                      id: 'apiKey' as TabType,
                      label: 'Clé API',
                      icon: MdOutlineVpnKey,
                      activeClasses:
                          'border-leadrs-brown-focus bg-leadrs-brown-soft text-leadrs-brown-focus',
                      iconBg: 'bg-leadrs-brown-focus',
                  },
              ]
            : []),
        {
            id: 'danger' as TabType,
            label: 'Zone de danger',
            icon: MdOutlineWarningAmber,
            activeClasses: 'border-leadrs-red-focus bg-leadrs-red-soft text-leadrs-red-focus',
            iconBg: 'bg-leadrs-red-focus',
        },
    ];

    return (
        <>
            <div className="mb-3 flex">
                <GoBack />
            </div>
            <div className="w-full">
                <div className="rounded-xl border border-leadrs-stroke bg-white px-6 py-6">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex items-start gap-3">
                            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-leadrs-purple-soft">
                                <MdOutlineSettings className="h-5 w-5 text-leadrs-brand" />
                            </div>
                            <div>
                                <h2 className="font-crimson text-2xl font-semibold leading-tight text-leadrs-text">
                                    Paramètres du compte
                                </h2>
                                <p className="mt-1 font-geist text-sm text-leadrs-text-soft">
                                    Modifiez vos informations de compte, votre facturation et vos préférences de sécurité.
                                </p>
                            </div>
                        </div>
                        <span
                            onClick={handleLogout}
                            className="cursor-pointer font-geist text-sm text-leadrs-red-focus underline hover:opacity-80"
                        >
                            Se déconnecter
                        </span>
                    </div>
                </div>

                <div className="mt-4 rounded-xl border border-leadrs-stroke bg-white px-4 py-4">
                    <div className="flex flex-wrap gap-2">
                        {tabs.map((tab) => (
                            (() => {
                                const Icon = tab.icon;
                                const isActive = activeTab === tab.id;
                                return (
                                    <button
                                        key={tab.id}
                                        onClick={() => setActiveTab(tab.id)}
                                        className={`flex items-center gap-2 rounded-xl border px-3 py-2 font-geist text-sm font-medium transition-all ${
                                            isActive
                                                ? `${tab.activeClasses} shadow-sm`
                                                : 'border-leadrs-stroke bg-leadrs-bg text-leadrs-text-soft hover:border-leadrs-text-soft hover:text-leadrs-text'
                                        }`}
                                    >
                                        <span
                                            className={`flex h-7 w-7 items-center justify-center rounded-lg ${
                                                isActive
                                                    ? tab.iconBg
                                                    : 'bg-white border border-leadrs-stroke'
                                            }`}
                                        >
                                            <Icon
                                                className={`h-4 w-4 ${
                                                    isActive
                                                        ? 'text-white'
                                                        : 'text-leadrs-text-soft'
                                                }`}
                                            />
                                        </span>
                                        <span>{tab.label}</span>
                                    </button>
                                );
                            })()
                        ))}
                    </div>
                </div>

                <div className="mt-4">
                    {activeTab === 'account' && (
                        <div className="flex flex-col gap-4">
                            <Information />
                            {user?.type === 'seller' && <AcquisitionPreferences />}
                            {user?.canSwitchAccountType && (
                                <div className="rounded-xl border border-leadrs-stroke bg-white px-5 py-5">
                                    <h3 className="font-geist text-base font-semibold text-leadrs-text">
                                        Compte {user?.type === 'seller' ? 'acheteur' : 'vendeur'} lié
                                    </h3>
                                    {linkedAccount ? (
                                        <p className="mt-2 font-geist text-sm text-leadrs-text-soft">
                                            Votre compte {linkedAccount.type === 'seller' ? 'vendeur' : 'acheteur'}
                                            {linkedAccount.email ? ` (${linkedAccount.email})` : ''} est lié à ce
                                            compte. Utilisez le bouton de bascule dans la barre du haut pour y
                                            accéder.
                                        </p>
                                    ) : (
                                        <>
                                            <p className="mt-2 font-geist text-sm text-leadrs-text-soft">
                                                Vous pouvez créer un compte {user?.type === 'seller' ? 'acheteur' : 'vendeur'} lié
                                                à celui-ci et basculer de l'un à l'autre en un clic.
                                            </p>
                                            {switchError && (
                                                <p className="mt-2 font-geist text-sm text-leadrs-red-focus">
                                                    {switchError}
                                                </p>
                                            )}
                                            <button
                                                onClick={handleActivateAccountSwitch}
                                                disabled={switchLoading}
                                                className="linear mt-3 rounded-lg bg-leadrs-brand px-4 py-1.5 text-sm font-medium text-white transition duration-200 hover:cursor-pointer disabled:opacity-50"
                                            >
                                                {switchLoading
                                                    ? 'Création...'
                                                    : `Devenir ${user?.type === 'seller' ? 'acheteur' : 'vendeur'}`}
                                            </button>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'invoice' && (
                        <div>
                            {user?.type === 'buyer' && <UpdateBuyerInvoiceInformation />}
                            {user?.type === 'seller' && <UpdateSellerInvoiceInformation />}
                        </div>
                    )}

                    {activeTab === 'security' && (
                        <div>
                            <Password />
                        </div>
                    )}

                    {activeTab === 'integration' && user?.type === 'seller' && (
                        <div>
                            <div className="rounded-xl border border-leadrs-stroke bg-white px-5 py-5">
                                <div>
                                    <h3 className="font-geist text-base font-semibold text-leadrs-text">
                                        Tester mon intégration
                                    </h3>
                                    <p className="mt-2 font-geist text-sm text-leadrs-text-soft">
                                        Vous pouvez à tout moment tester la
                                        connexion entre votre landing page et
                                        leadrs.co.
                                    </p>
                                    <p className="mt-2 font-geist text-sm text-leadrs-text-soft">
                                        Pour se faire indiquer la valeur{' '}
                                        <b>'testing@leadrs.co'</b> dans n'importe
                                        lequel des champs de votre formulaire.
                                    </p>
                                    <p className="mt-2 font-geist text-sm text-leadrs-text-soft">
                                        Si vous êtes correctement intégré vous
                                        recevrez un mail de confirmation, sinon
                                        modifiez l'url de votre landing page et
                                        suivez le guide pour rétablir la connexion.
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'apiKey' && user?.type === 'buyer' && (
                        <div>
                            <ApiKeyPanel />
                        </div>
                    )}

                    {activeTab === 'apiKey' && user?.type === 'seller' && (
                        <div>
                            <SellerApiKeyRedirect />
                        </div>
                    )}

                    {activeTab === 'danger' && (
                        <div>
                            <div className="flex flex-row items-center justify-between rounded-xl border border-leadrs-red-focus bg-leadrs-red-soft px-5 py-4">
                                <div>
                                    <h3 className="font-geist text-base font-semibold text-leadrs-red-focus">
                                        Supprimer ce compte
                                    </h3>
                                    <p className="font-geist text-sm text-leadrs-red-focus">
                                        Attention cette action est irréversible
                                    </p>
                                    {wantToDelete && (
                                        <div className="mt-3">
                                            <p className="font-geist text-xs text-leadrs-red-focus">
                                                Vous pouvez faire supprimer votre compte en
                                                envoyant un mail à l'adresse suivante:
                                                charlotte@leadrs.co
                                            </p>
                                        </div>
                                    )}
                                </div>
                                <button
                                    className="linear flex cursor-pointer items-center justify-center rounded-lg border border-leadrs-red-focus bg-white px-3 py-1.5 font-geist text-sm font-medium text-leadrs-red-focus transition duration-200 hover:opacity-80"
                                    onClick={() => setWantToDelete(!wantToDelete)}
                                >
                                    Supprimer
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default ProfileSetting;
